import { useState } from 'react';

import { FileClock, Clock, CheckCircle2, AlertTriangle, ArrowRight } from 'lucide-react';

import { SettingsWrapper } from './SettingsWrapper';
import UnprocessedDocsTable from './UnprocessedDocsTable';
import { RunType } from '../../../../../../../backend/src/case/run/run-type';
import { Run } from '@/api/types/backend-dto-types';
import { Button } from '@/components/atoms/new-button';
import formatDate from '@/helpers/formatDate';

interface RunsOverviewProps {
  caseId: string;
  runs: Run[];
}

const getRunTypeChip = (run: Run) => {
  switch (run.type) {
    case RunType.Regular:
      return (
        <span className="rounded-md bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-gray-200">
          Regular
        </span>
      );
    case RunType.AddDocs:
      return (
        <span className="rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-blue-200">
          New Documents
        </span>
      );
    case RunType.AddDocsUpdateSynopsis:
      return (
        <span className="rounded-md bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-indigo-200">
          New Documents & Updated Details
        </span>
      );
    case RunType.UpdateSynopsis:
      return (
        <span className="rounded-md bg-purple-50 px-2 py-0.5 text-xs font-medium text-purple-700 ring-1 ring-purple-200">
          Updated Details
        </span>
      );
    case RunType.UpdateInconsistencies:
      return (
        <span className="rounded-md bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-700 ring-1 ring-orange-200">
          Updated Inconsistencies
        </span>
      );
    case RunType.DeleteDocuments:
      return (
        <span className="rounded-md bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-red-200">
          Deleted Documents
        </span>
      );
    default:
      return <></>;
  }
};

const RunsOverview = ({ caseId, runs }: RunsOverviewProps) => {
  const [showFailedDocs, setShowFailedDocs] = useState(false);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);

  // Get the selected run for context
  const selectedRun = selectedRunId ? runs?.find((run) => run.id === selectedRunId) : null;

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  if (showFailedDocs) {
    return (
      <SettingsWrapper>
        {selectedRunId && (
          <UnprocessedDocsTable
            caseId={caseId}
            failedPagesCount={selectedRun?.failedPagesCount || 0}
            runId={selectedRunId}
            isVisible={showFailedDocs}
            onBackToRuns={() => {
              setShowFailedDocs(false);
              setSelectedRunId(null);
            }}
            selectedRun={selectedRun || undefined}
          />
        )}
      </SettingsWrapper>
    );
  }

  return (
    <SettingsWrapper>
      <div className="mb-4">
        <div className="mb-2 flex items-center gap-2">
          <h2 className="text-base font-semibold">Case Runs</h2>
        </div>
        <p className="text-sm text-gray-500">View the status of previous submissions on this case.</p>
      </div>

      <div className="h-full overflow-y-auto rounded-md border border-gray-200 bg-white">
        <div className="divide-y divide-gray-100">
          {runs?.map((run, index) => (
            <div key={run.id} className="group flex items-center justify-between px-4 py-3 hover:bg-gray-50">
              <div className="flex flex-1 items-center gap-3">
                <div className="flex h-7 w-7 items-center justify-center rounded bg-gray-100 text-xs font-semibold text-gray-700 group-hover:bg-gray-200">
                  {index + 1}
                </div>
                <div className="flex flex-1 flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {run.inQueue ? (
                        <span className="text-sm font-medium text-yellow-600">
                          In queue, position {run.queuePosition}
                        </span>
                      ) : (
                        <span className="text-sm font-medium text-gray-900">
                          {run.status && run.status?.charAt(0).toUpperCase() + run.status?.slice(1)}
                        </span>
                      )}
                      {run.createdBy && (
                        <span className="rounded-md bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-blue-200">
                          {run.createdBy.email}
                        </span>
                      )}
                      {index === runs.length - 1 && (
                        <span className="rounded-md bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-gray-200">
                          Initial Submission
                        </span>
                      )}
                      {getRunTypeChip(run)}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <div className="flex items-center gap-1.5">
                      <Clock className="h-3 w-3" />
                      <span>Started {formatDate(run.createdDate, true, true)}</span>
                    </div>
                    {run.completedAt && (
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                        <span>Completed {formatDate(run.completedAt, true, true)}</span>
                      </div>
                    )}
                    {run.totalPages > 0 && run.completedAt && (
                      <div className="flex gap-2 text-xs font-normal">
                        <span className="text-gray-700">{formatNumber(run.totalPages)} pages</span>
                        <span className="text-green-600">
                          {formatNumber(run.successfulPagesCount)} pages successful
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="ml-4 flex items-center gap-4">
                {run.failedPagesCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowFailedDocs(true);
                      setSelectedRunId(run.id);
                    }}
                    className="group h-auto border border-red-200 px-2 py-1 text-xs text-red-600 hover:border-red-300 hover:bg-red-50 hover:text-red-800"
                  >
                    <AlertTriangle className="mr-1 h-3 w-3" />
                    {formatNumber(run.failedPagesCount)} pages failed
                    <ArrowRight className="h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </Button>
                )}
                {run.completedAt ? (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                ) : run.inQueue ? (
                  <Clock className="h-4 w-4 text-yellow-500" />
                ) : (
                  <FileClock className="h-4 w-4 text-blue-500" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </SettingsWrapper>
  );
};

export default RunsOverview;
